import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { usePillSelection } from 'components/WorkspaceEditor/features/pillSelection/hooks/usePillSelection'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import type { DataNode } from 'components/WorkspaceEditor/types'
import {
    getStatusClass,
    isManufacturerOrBODPill,
} from 'components/WorkspaceEditor/utils'
import { getImportTypeInfo } from 'components/WorkspaceEditor/utils/importTypeUtils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import {
    AssemblyText,
    Hyperlink,
    OptionGroup,
    PlainText,
    ReplacementOption,
    SelectionOption,
    UnitsOfMeasure,
} from '.'
import {
    isKeyedContent,
    isKeyedContentForContentItem,
} from '../ContentEditor/contentUtils'
import ProductGroupIcon from '../TreeView/ProductGroupIcon'
import ProductTypeIcon from '../TreeView/ProductTypeIcon'
import { shouldSkipForKeyColonSpacing } from './PlainText/utils/textUtils'
import { useCohesiveContentWrapperStyles } from './useNodeContentRendererStyles'

/** Props for rendering node item */
export interface NodeContentRendererProps {
    /** The node to render */
    node: DataNode
    /** Whether the node is editable */
    editable?: boolean
    /** Whether the node is inactive */
    inactive?: boolean
}

/**
 * Renders the node item for a tree node
 * @param {NodeContentRendererProps} NodeContentRendererProps - Props for rendering node item
 * @returns {ReactElement} Styled node item
 */
export const NodeContentRenderer = memo<NodeContentRendererProps>(
    ({ node, editable = false, inactive = false }) => {
        const {
            type,
            contentItemNodes,
            isProductGroup,
            isProductType,
            isDisabled,
            isCustomManufacturer,
        } = node
        const {
            displayContent,
            articleTypography,
            paragraphTypography,
            inactiveText,
            addedText,
            removedText,
            readOnlyCursor,
            assemblyTextField,
            inputText,
            productGroupIcon,
            productTypeIcon,
            productIconWrapper,
        } = useTreeViewStyles()
        const { cohesiveContentWrapper } = useCohesiveContentWrapperStyles()

        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

        const {
            visibilitySettingValues: {
                areTrackChangesVisible,
                isInactiveContentVisible,
                arePTLinksVisible,
            },
        } = useShallowEditorActionBar((state) => ({
            visibilitySettingValues: state.visibilitySettingValues,
        }))

        const { selectedProjectElementNode } = useShallowSelector((state) => ({
            selectedProjectElementNode: state.selectedProjectElementNode,
        }))

        const { handlePillClick, handleNewPillDelete } = usePillSelection()

        // Get import type info for the selected element
        const importInfo = useMemo(
            () => getImportTypeInfo(selectedProjectElementNode),
            [selectedProjectElementNode]
        )

        // Check if manufacturer pills should be disabled based on feature flag
        // For Project Import, OM Import, and OM/MasterSpec Clone, enable pill click selection (show full list, allow interaction)
        const isManufacturerPill = isManufacturerOrBODPill(node)
        const isFeatureFlagEnabled =
            processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1'
        const shouldDisableManufacturerPills =
            isManufacturerPill &&
            isFeatureFlagEnabled &&
            !importInfo.isProjectImport &&
            !importInfo.isOMImport &&
            !importInfo.isMasterSpecClone
        const shouldApplyDynamicManufacturerSelection =
            isFeatureFlagEnabled &&
            (!!importInfo.isMasterSpecClone || !!importInfo.isOMImport)

        const plainTextClass = useMemo(
            () =>
                type === NODE_TYPES.Article
                    ? articleTypography
                    : paragraphTypography,
            [type]
        )
        const shouldHideDeleted = (change: string) =>
            change === 'DELETED' && !areTrackChangesVisible
        const shouldHideEmptyManufacturer = useCallback(
            (contentItem: Partial<TextContentElement>) => {
                const isOptionGroup =
                    contentItem.type === NODE_TYPES.OptionGroup
                const isManufacturerOrBOD = isManufacturerOrBODPill(node)
                const isEmpty =
                    contentItem.text?.trim() === '' ||
                    contentItem.text?.trim() === '.'

                return isManufacturerOrBOD && !isOptionGroup && isEmpty
            },
            [node]
        )

        const renderProductIcon = useCallback(
            (tooltipText?: string, isFirstTextItem?: boolean) => {
                if (!isFirstTextItem) return null
                if (isCurrentlyOnOMWorkspace && !arePTLinksVisible) return null

                if (isProductGroup) {
                    return (
                        <HoverTooltip
                            text={`Product Group: ${tooltipText}`}
                            placements={['top', 'left']}
                            className={productGroupIcon}
                        >
                            <span className={productIconWrapper}>
                                <ProductGroupIcon
                                    className={productGroupIcon}
                                />
                            </span>
                        </HoverTooltip>
                    )
                }

                if (isProductType) {
                    return (
                        <HoverTooltip
                            text={`Product Type: ${tooltipText}`}
                            placements={['top', 'left']}
                            className={productTypeIcon}
                        >
                            <span className={productIconWrapper}>
                                <ProductTypeIcon className={productTypeIcon} />
                            </span>
                        </HoverTooltip>
                    )
                }

                return null
            },
            [
                isProductGroup,
                isProductType,
                isCurrentlyOnOMWorkspace,
                arePTLinksVisible,
            ]
        )

        // Trims leading/trailing spaces from ProductType first items to fix key:colon spacing
        const trimContentItem = useCallback(
            (item: Partial<TextContentElement>, isFirstTextItem: boolean) =>
                isProductType && isFirstTextItem && item.text
                    ? { ...item, text: item.text.trim() }
                    : item,
            [isProductType]
        )
        // User-added custom MFR: exempt from parent inactive state (Bug 2684621)
        const isUserAddedCustomMfr =
            isCustomManufacturer &&
            !node.contentItemView?.isAddedInOfficeMasterDraftProject

        const omCustomManufacturer = useMemo(
            () =>
                node.isCustomManufacturer &&
                (isCurrentlyOnOMWorkspace ||
                    node?.contentItemView?.isAddedInOfficeMasterDraftProject),
            [node, isCurrentlyOnOMWorkspace]
        )
        return (
            <div
                className={clsx(cohesiveContentWrapper, displayContent)}
                data-cohesive-content="true"
            >
                {contentItemNodes.map((contentItem, index) => {
                    const shouldHideInactiveContent =
                        !isInactiveContentVisible &&
                        contentItem.optionItemState === 'InActive'

                    // Existing data fix [2556847]: Product Types created from the Insert Drawing Designation tag
                    // generated an inline IDD text field, resulting in a duplicate — one inline, one as the default.
                    // This hides the inline text field and the accompanying duplicate colon.
                    const isLegacyDrawingDesignationDuplicateColon =
                        (contentItem?.elements?.[0]?.placeholderText ===
                            'Insert Drawing Designation' &&
                            index === 1) ||
                        contentItem?.text?.trim() === ':'

                    if (isLegacyDrawingDesignationDuplicateColon) {
                        return null
                    }

                    if (
                        shouldHideEmptyManufacturer(contentItem) ||
                        // Don't render deleted content when track changes are not visible
                        shouldHideDeleted(
                            contentItem?.changeType?.toUpperCase() ?? ''
                        ) ||
                        // Don't render inactive options when inactive content visibility is off
                        shouldHideInactiveContent ||
                        // Skip duplicate colon after FieldRef or whitespace before FieldRef/colon
                        shouldSkipForKeyColonSpacing(
                            contentItem,
                            index,
                            contentItemNodes,
                            !!isProductType
                        )
                    ) {
                        return null
                    }

                    switch (contentItem.type) {
                        case NODE_TYPES.OptionGroup:
                            return (
                                <OptionGroup
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    handlePillClick={handlePillClick}
                                    parentOptionGroupId={
                                        node?.contentItemView?.id ?? ''
                                    }
                                    onNewPillDelete={handleNewPillDelete}
                                />
                            )
                        case NODE_TYPES.SelectionOption:
                            return isKeyedContent(contentItemNodes) ? (
                                <PlainText
                                    contentItem={contentItem}
                                    node={node}
                                    inactive={inactive}
                                    editable={editable}
                                    classes={clsx(
                                        plainTextClass,
                                        inactive && inactiveText,
                                        !editable && readOnlyCursor
                                    )}
                                />
                            ) : (
                                <SelectionOption
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    onSelectionOptionClick={handlePillClick}
                                />
                            )
                        case NODE_TYPES.Hyperlink:
                            return (
                                <Hyperlink
                                    contentItem={contentItem}
                                    isKeyedHyperlink={isKeyedContentForContentItem(
                                        contentItem
                                    )}
                                    node={node}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    disableCursorOnly={
                                        shouldDisableManufacturerPills
                                    }
                                />
                            )
                        case NODE_TYPES.UnitsOfMeasure:
                            return (
                                <UnitsOfMeasure
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    parentContentItem={
                                        node.contentItemView as Partial<
                                            TextContentElement
                                        >
                                    }
                                />
                            )
                        case NODE_TYPES.ReplacementOption:
                            return (
                                <ReplacementOption
                                    contentItem={contentItem}
                                    inactive={!isUserAddedCustomMfr && inactive}
                                    disabled={!!isDisabled}
                                    editable={editable}
                                    isCustomManufacturer={isCustomManufacturer}
                                    isPillSelected={
                                        !!node.contentItemView
                                            ?.isPillSelected ||
                                        // OM Import/MS Clone: user-added custom MFRs pre-selected
                                        !!(
                                            isUserAddedCustomMfr &&
                                            shouldApplyDynamicManufacturerSelection
                                        )
                                    }
                                    enableDynamicManufacturerSelection={
                                        shouldApplyDynamicManufacturerSelection
                                    }
                                    isCustomMFR={omCustomManufacturer}
                                    node={node}
                                />
                            )
                        case NODE_TYPES.AssemblyText:
                            return (
                                <AssemblyText
                                    contentItem={contentItem}
                                    editable={editable && !inactive}
                                    classes={clsx(assemblyTextField, inputText)}
                                />
                            )
                        default:
                            // Only render the product icon for the first PT text item
                            const isFirstTextItem = index === 0
                            const statusClass = getStatusClass(
                                inactive,
                                contentItem.changeType?.toUpperCase() ?? '',
                                areTrackChangesVisible,
                                {
                                    inactiveText,
                                    addedText,
                                    removedText,
                                }
                            )
                            const item = trimContentItem(
                                contentItem,
                                isFirstTextItem
                            )

                            return (
                                <>
                                    {renderProductIcon(
                                        item.text,
                                        isFirstTextItem
                                    )}
                                    <PlainText
                                        contentItem={item}
                                        node={node}
                                        inactive={inactive}
                                        editable={editable}
                                        classes={clsx(
                                            plainTextClass,
                                            statusClass,
                                            !editable && readOnlyCursor
                                        )}
                                    />
                                </>
                            )
                    }
                })}
            </div>
        )
    }
)
